import { Injectable, OnDestroy } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  filter,
  map,
  merge,
  of,
  pairwise,
  skip,
  Subject,
  Subscription,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import { TranslateService } from '@ngx-translate/core';
import { INFOS } from '../common/utils/notification-constants';
import { EVENT_STATUS } from '../models/event';
import { PanelView } from '../sessions/panel/panel.component';
import {
  AttendanceUser,
  EventWithTimingInfo,
} from '../sessions/session/attendance/attendance-ui-models';
import { formatMillis } from '../sessions/session/attendance/attendance-utils';
import { SpaceRepository } from '../state/space.repository';
import { IconBackground } from '../ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import { EVENT_TIMING_GRACE_MARGIN_MS } from '../app.constants';
import { RedisPresenceV2 } from './presence_v2.service';
import { AttendanceService } from './attendance.service';
import { FLAGS, FlagsService } from './flags.service';
import { ForegroundActivityService } from './foreground-activity.service';
import {
  NotificationToasterService,
  NotificationActionButton,
  NotificationIconTypes,
  NotificationIconConfig,
} from './notification-toaster.service';
import { RealtimeService } from './realtime.service';
import { SessionSharedDataService } from './session-shared-data.service';
import { SpacesService } from './spaces.service';
import { TelemetryService } from './telemetry.service';
import { TimerService } from './timer.service';
import { ObserverModeService } from './observer-mode.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AttendanceManagerService implements OnDestroy {
  private readonly MAX_TIMEOUT_DURATION_IN_MILLI_SECONDS = 1000 * 60 * 60 * 12;

  private readonly eventRealtimeDataTypes = ['event'];
  private readonly attendanceRealtimeDataTypes = [
    'user_attendance_update',
    'user_joins_attendance_event',
  ];
  private timeout?: NodeJS.Timer;
  private events: EventWithTimingInfo[] = [];
  private joinedOnGoingEvent = false;
  private lastFetchDate = new Date();
  private prevShowedNotificationEventId?: string;
  private foregroundSubscription?: Subscription;

  // Observables/Subjects
  public attendanceFlags$ = this.flagsService.featureFlagChanged(FLAGS.PROJECT_SAM).pipe(
    map((flagStatus) => ({
      markAttendance:
        flagStatus && !!this.flagsService.featureFlagsVariables.project_sam.space_attendance,
      attendanceList:
        flagStatus && !!this.flagsService.featureFlagsVariables.project_sam.space_attendance_list,
      logAttendance:
        flagStatus && !!this.flagsService.featureFlagsVariables.project_sam.attendance_logging,
    })),
    tap((flags) => {
      this.logAttendance = flags.logAttendance;
    }),
  );

  private attendanceEnabledSpaceId$ = combineLatest([
    this.attendanceFlags$,
    this.spaceRepo.activeSyncedOrLeftSpaceId$,
  ]).pipe(
    map(([attendanceFlags, id]) => (attendanceFlags.markAttendance && !!id ? id : undefined)),
  );

  private _onGoingEvent$ = new BehaviorSubject<EventWithTimingInfo | undefined>(undefined);
  private refresh$ = new Subject<string | undefined>();
  private resetTrigger$ = this.attendanceEnabledSpaceId$.pipe(filter((id) => !id));
  private _eventEnded$ = new Subject<string>();
  public eventEnded$ = this._eventEnded$.asObservable();

  // Fetching Events in Space
  private fetchEventsTrigger$ = merge(
    this.attendanceEnabledSpaceId$,
    this.realtimeService.realtimeInitOrReconnect$,
    this.refresh$,
  ).pipe(
    withLatestFrom(this.attendanceEnabledSpaceId$),
    map(([_, id]) => id),
    filter((id) => !!id),
    switchMap((id) =>
      this.spacesService
        .getAttendanceEventsInSpace(id!, this.MAX_TIMEOUT_DURATION_IN_MILLI_SECONDS)
        .pipe(
          catchError((error) => {
            this.log({ message: 'Error in fetching current event in space', error });
            return of([]);
          }),
        ),
    ),
  );

  private realtimeUpdateTrigger$ = merge(of(undefined), this.attendanceEnabledSpaceId$).pipe(
    pairwise(),
    tap(([oldSpaceId, _]) => {
      !!oldSpaceId && this.realtimeService.unsubscribeSession(oldSpaceId);
    }),
    map(([_, newSpaceId]) => newSpaceId),
    switchMap((spaceId) => {
      const realtimeToken = spaceId
        ? this.sessionSharedDataService.sessionAuthData[spaceId]?.realtimeToken
        : undefined;
      if (!realtimeToken) {
        return of();
      }
      return this.realtimeService.subscribeSession(spaceId!, realtimeToken).pipe(
        catchError((error) => {
          this.log({ message: 'Error in subscribing to faye session', error });
          return of();
        }),
      );
    }),
    filter(
      (value) =>
        !!value &&
        (this.eventRealtimeDataTypes.includes(value.dataType) ||
          this.attendanceRealtimeDataTypes.includes(value.dataType)),
    ),
  );

  private _attendanceRealtimeUpdates$ = new Subject<AttendanceUser>();
  public attendanceRealtimeUpdates$ = this._attendanceRealtimeUpdates$.asObservable();

  public onGoingEvent$ = this._onGoingEvent$.asObservable();
  private logAttendance = false;

  // Private Methods
  constructor(
    private translateService: TranslateService,
    private notificationToasterService: NotificationToasterService,
    private timerService: TimerService,
    private spaceRepo: SpaceRepository,
    private spacesService: SpacesService,
    private attendanceService: AttendanceService,
    private flagsService: FlagsService,
    private realtimeService: RealtimeService,
    private sessionSharedDataService: SessionSharedDataService,
    private telemetryService: TelemetryService,
    private presenceV2Service: RedisPresenceV2,
    private foregroundActivityService: ForegroundActivityService,
    private observerModeService: ObserverModeService,
  ) {
    this.realtimeUpdateTrigger$.pipe(untilDestroyed(this)).subscribe((value) => {
      this.log({ message: 'realtime update', updateObject: value });
      if (this.attendanceRealtimeDataTypes.includes(value.dataType)) {
        this._attendanceRealtimeUpdates$.next(value.data);
      } else {
        this.refetchEvents();
      }
    });
    this.resetTrigger$.pipe(untilDestroyed(this)).subscribe(() => {
      this.reset();
    });
    this.fetchEventsTrigger$.pipe(untilDestroyed(this)).subscribe((events) => {
      this.lastFetchDate = new Date();
      this.events = events;
      this.checkIfOngoingEvent();
    });
    this.presenceV2Service.loggedInUserIdleState$
      .pipe(
        skip(1), // Skip the initial BehaviorSubject emission
        filter((isActive) => isActive), // Only proceed when user becomes active after being inactive
        untilDestroyed(this),
      )
      .subscribe(() => {
        if (this.shouldCheckForOngoingEvent()) {
          this.log({
            message:
              'Logged in user became active after being idle. Checking if we need to mark attendance.',
          });
          this.checkIfOngoingEvent();
        }
      });
  }

  /**
   * Checks if we should attempt to check for ongoing events and mark attendance.
   * Used by recovery mechanisms (idle detection, tab visibility) to avoid unnecessary calls.
   */
  private shouldCheckForOngoingEvent(): boolean {
    return Boolean(
      this.spaceRepo.activeSpace && this.events.length > 0 && !this.joinedOnGoingEvent,
    );
  }

  /**
   * Start listening to tab visibility changes - recovery mechanism for missed timeouts when tab is backgrounded.
   * This covers the gap where user is inactive for < 5 minutes (below USER_IDLE_THRESHOLD in presenceV2Service).
   * Called when we set a timeout for an upcoming event, so we can recover if that timeout gets throttled.
   */
  private startListeningToForeground(): void {
    // Avoid duplicate subscriptions
    if (this.foregroundSubscription && !this.foregroundSubscription.closed) {
      this.log({
        message: 'Already listening to foreground changes, skipping duplicate subscription',
      });
      return;
    }

    this.log({ message: 'Starting to listen to tab visibility changes' });
    this.foregroundSubscription = this.foregroundActivityService.isForegroundInactive$
      .pipe(
        filter((isInactive) => !isInactive), // Only proceed when tab becomes visible (not hidden)
        debounceTime(500), // Debounce rapid tab switches
      )
      .subscribe(() => {
        if (this.shouldCheckForOngoingEvent()) {
          this.log({
            message:
              'Tab became visible after being hidden. Checking if we need to mark attendance.',
            eventsCount: this.events.length,
            nextEventTitle: this.events[0]?.title,
          });
          this.checkIfOngoingEvent();
        }
      });
  }

  /**
   * Stop listening to tab visibility changes.
   * Called when attendance is successfully marked or when cleanup is needed.
   */
  private stopListeningToForeground(): void {
    if (this.foregroundSubscription) {
      this.log({ message: 'Stopping tab visibility listener' });
      this.foregroundSubscription.unsubscribe();
      this.foregroundSubscription = undefined;
    }
  }

  private async checkIfOngoingEvent() {
    if (!this.events.length) {
      this.log({ message: 'events array is empty' });
      this.reset();
      return;
    }
    let { eventStartTimeOffsetMillis, eventEndtTimeOffsetMillis } = this.events[0];
    const millisSinceLastFetch = Date.now() - this.lastFetchDate.getTime();
    eventStartTimeOffsetMillis += millisSinceLastFetch;
    eventEndtTimeOffsetMillis -= millisSinceLastFetch;

    // Earliest event isn't started yet
    // eventStartTimeOffsetMillis goes from -ve to +ve
    // We know event hasn't started until it goes +ve
    // But since timers can run early, there is a chance that eventStartTimeOffsetMillis is still -ve
    // So we use EARLY_TIMEOUT_THRESHOLD_IN_MILLI_SECONDS * -1 to check if event has started
    if (eventStartTimeOffsetMillis <= EVENT_TIMING_GRACE_MARGIN_MS * -1) {
      this.log({
        message: "event isn't started",
        eventTitle: this.events[0].title,
        timeToStart: `${formatMillis(-eventStartTimeOffsetMillis)}`,
        eventStartTimeOffsetMillis,
      });
      this.setTimeout(eventStartTimeOffsetMillis);
      this.startListeningToForeground(); // Start listening for tab switches in case timeout gets throttled
      this._onGoingEvent$.next(undefined);
      this.joinedOnGoingEvent = false;
    }
    // Earliest event already ended
    // eventEndtTimeOffsetMillis goes from +ve to -ve
    // When it goes -ve, we know event has ended
    // But since timers can run early, there is a chance that eventEndtTimeOffsetMillis is still +ve
    // So we use EARLY_TIMEOUT_THRESHOLD_IN_MILLI_SECONDS to check if event has ended
    else if (eventEndtTimeOffsetMillis <= EVENT_TIMING_GRACE_MARGIN_MS) {
      this.log({
        message: 'event ended',
        eventTitle: this.events[0].title,
        timeToEnd: `${formatMillis(-eventEndtTimeOffsetMillis)}`,
        eventEndtTimeOffsetMillis,
      });

      this._eventEnded$.next(this.events[0]._id);
      this.events = this.events.slice(1);
      this._onGoingEvent$.next(undefined);
      this.joinedOnGoingEvent = false;
      this.checkIfOngoingEvent();
    }
    // Earliest event is ongoing and attendance isn't marked yet
    // Make the call to mark the attendance and then re-check if the event is still ongoing or not
    // Because if eventEndtTimeOffsetMillis is too small the event might end while making the API call to update attendance
    else if (!this.joinedOnGoingEvent) {
      if (!this.presenceV2Service.isLoggedInUserActive()) {
        this.log({
          message: 'event is ongoing, but user is not active, deferring attendance marking',
          eventTitle: this.events[0].title,
        });
        this.setTimeout(eventEndtTimeOffsetMillis);
        return;
      }

      // mimic the success path without actually marking attendance to keep the same flow
      if (this.observerModeService.isObserverModeActive()) {
        this.log({
          message: 'Observer mode active, skipping attendance marking',
          eventTitle: this.events[0].title,
        });
        this.handleAttendanceResponse();
        return;
      }

      this.log({
        message: 'event is ongoing, making mark attendance API call',
        eventTitle: this.events[0].title,
        timeToStart: `${formatMillis(eventStartTimeOffsetMillis)}`,
        timeToEnd: `${formatMillis(eventEndtTimeOffsetMillis)}`,
        eventStartTimeOffsetMillis,
        eventEndtTimeOffsetMillis,
      });
      this.attendanceService.attendEvent(this.events[0]._id).subscribe((eventAttendance) => {
        this.handleAttendanceResponse();

        if (eventAttendance) {
          this.log({
            message: 'attendEvent Response',
            eventAttendance: eventAttendance,
          });
        } else {
          this.telemetryService.event(
            "User trying to hit '/attendee/:eventId' endpoint after event ended",
            {
              message: 'attendEvent Response is Empty',
              eventTitle: this.events[0].title,
              eventID: this.events[0]._id,
              eventEndTime: this.events[0].end_time,
              clientTime: new Date(),
              timeToStart: `${formatMillis(eventStartTimeOffsetMillis)}`,
              timeToEnd: `${formatMillis(eventEndtTimeOffsetMillis)}`,
              eventStartTimeOffsetMillis,
              eventEndtTimeOffsetMillis,
            },
          );
        }
      });
    }
    // Earliest event is ongoing and attendance is marked, it's safe to emit the onGoingEvent to subscribers
    else {
      const currentOngoingEvent = this.events[0];
      // Set to false for the upcoming event
      this.joinedOnGoingEvent = false;
      currentOngoingEvent.startTimeOnClient = new Date(Date.now() - eventStartTimeOffsetMillis);
      this.log({
        message: 'event is ongoing, setting the timeout',
        eventTitle: currentOngoingEvent.title,
        timeToStart: formatMillis(eventStartTimeOffsetMillis),
        timeToEnd: formatMillis(eventEndtTimeOffsetMillis),
        timeSinceLastFetch: formatMillis(millisSinceLastFetch),
        startTimeOnClient: currentOngoingEvent.startTimeOnClient,
        lastFetchDate: this.lastFetchDate,
        eventStartTimeOffsetMillis,
        eventEndtTimeOffsetMillis,
      });
      // For cancelled events, attendance is still silently marked above but we don't
      // emit the ongoing event. This prevents all downstream consumers (notification,
      // auto-start call, recording state, event countdown, red dot indicator) from
      // reacting to a cancelled event.
      if (currentOngoingEvent.state?.status !== EVENT_STATUS.CANCELLED) {
        this._onGoingEvent$.next(currentOngoingEvent);
        this.showEventStartedNotification(currentOngoingEvent);
      }
      this.setTimeout(eventEndtTimeOffsetMillis);
    }
  }

  private handleAttendanceResponse() {
    this.joinedOnGoingEvent = true;
    this.stopListeningToForeground(); // Stop listening once attendance is marked
    this.checkIfOngoingEvent();
  }

  private reset() {
    this._onGoingEvent$.next(undefined);
    this.joinedOnGoingEvent = false;
    this.stopListeningToForeground();
    this.clearTimeout();
    this.events = [];
    this.prevShowedNotificationEventId = undefined;
    this.dismissEventStartedNotification();
  }

  private async clearTimeout() {
    if (this.timeout) {
      await this.timerService.clearTimeout(this.timeout);
    }
  }

  private async setTimeout(durationInMillis: number) {
    durationInMillis = Math.abs(durationInMillis);
    if (durationInMillis > this.MAX_TIMEOUT_DURATION_IN_MILLI_SECONDS) {
      this.log({ message: 'timeout not set, durationInMillis exceeds MAX_TIMOUT_DURATION' });
      return;
    }
    await this.clearTimeout();
    this.timeout = await this.timerService.setTimeout(() => {
      this.checkIfOngoingEvent();
    }, durationInMillis);
  }

  public dismissEventStartedNotification(): void {
    this.notificationToasterService.dismissNotificationsByCode([INFOS.EVENT_STARTED_IN_SPACE]);
  }

  // Public Methods
  log(logObject: { [key: string]: any }) {
    if (!this.logAttendance) {
      return;
    }
    const timestamp = new Date().toISOString();
    const logStr = Object.keys(logObject)
      .map((key) => `${key}=${JSON.stringify(logObject[key])}`)
      .join('\r\n');

    this.telemetryService.log(
      'debug',
      `[attendance-monitoring] - timestamp=${timestamp} - ${logStr}`,
    );
  }

  async ngOnDestroy() {
    await this.clearTimeout();
    this.stopListeningToForeground();
  }

  public refetchEvents() {
    this.refresh$.next(this.spaceRepo.activeSpace?._id);
  }

  public showEventStartedNotification(event: EventWithTimingInfo) {
    if (
      !this.spaceRepo.isCurrentUserHost() ||
      event._id === this.prevShowedNotificationEventId ||
      this.sessionSharedDataService.rightPanelView.getValue()?.panelView ===
        PanelView.participantsManager
    ) {
      return;
    }

    this.prevShowedNotificationEventId = event._id;

    const message = this.observerModeService.isObserverModeActive()
      ? this.translateService.instant('This Space has an active event.')
      : this.translateService.instant('This Space has an active event. Click to view attendance');

    const primaryAction: NotificationActionButton = {
      text: this.translateService.instant('View Attendance'),
      onClick: () => {
        this.dismissEventStartedNotification();
        this.sessionSharedDataService.changeRightPanelView.next(PanelView.participantsManager);
      },
    };

    const infoIconConfig: NotificationIconConfig = {
      assetName: 'info',
      type: NotificationIconTypes.MAT_ICON,
      background: IconBackground.INFO,
      outlined: true,
    };

    this.notificationToasterService.showDefaultSuccessNotification(
      message,
      INFOS.EVENT_STARTED_IN_SPACE,
      false, // activateTick
      undefined, // toastClass
      (builder) =>
        builder
          .timeOut(30) // Set timeout to 30 seconds
          .actionButtonsConfig({
            // Add the action button
            primary: primaryAction,
          })
          .icon(infoIconConfig),
    );
  }
}
